import { BffReport, Project } from "vmd-types";

import { STRAPI_URL } from "../env";
import {
  appQueryClient,
  fetchWithCache,
  invalidateAppQueries,
} from "./query-cache";

export async function fetchProject(project: {
  id: string;
}): Promise<BffReport | undefined> {
  const { id } = project;
  const response = await fetch(`${STRAPI_URL}/project/${id}`, {
    credentials: "include",
  });

  if (response.status >= 400) {
    throw new Error(`Fetch failed with status ${response.status}.`);
  }

  const json = (await response.json()) as Project;

  return { project: json };
}

export async function fetchAllProjects(): Promise<Project[] | undefined> {
  return fetchWithCache({
    queryKey: ["projects"],
    staleTime: 15 * 60 * 1000,
    fallback: appQueryClient.getQueryData<Project[]>(["projects"]),
    queryFn: async () => {
      const response = await fetch(`${STRAPI_URL}/project`, {
        credentials: "include",
      });

      if (response.status >= 400) {
        throw new Error(`Fetch failed with status ${response.status}.`);
      }

      return (await response.json()) as Project[];
    },
  });
}

export async function updateProject(
  id: number | string | undefined,
  project: Project,
  isOnReport: boolean
): Promise<Project | undefined> {
  try {
    const response = await fetch(`${STRAPI_URL}/project/${String(id)}`, {
      body: JSON.stringify({ project, isOnReport }),
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      method: "PUT",
    });

    if (!response.ok) {
      throw new Error(`save project failed with status ${response.status}.`);
    }
    const json = (await response.json()) as Project;
    await invalidateAppQueries(["project"]);
    await invalidateAppQueries(["projects"]);
    await invalidateAppQueries(["overlay"]);
    await invalidateAppQueries(["overview"]);
    await invalidateAppQueries(["dashboard"]);
    return json;
  } catch {
    return undefined;
  }
}

export async function createProject(
  project: Project
): Promise<Project | undefined> {
  try {
    const response = await fetch(`${STRAPI_URL}/project`, {
      body: JSON.stringify(project),
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      method: "POST",
    });

    if (!response.ok) {
      throw new Error(`save project failed with status ${response.status}.`);
    }
    const json = (await response.json()) as Project;
    await invalidateAppQueries(["project"]);
    await invalidateAppQueries(["projects"]);
    await invalidateAppQueries(["overlay"]);
    await invalidateAppQueries(["overview"]);
    await invalidateAppQueries(["dashboard"]);
    return json;
  } catch {
    return undefined;
  }
}

export async function deleteProject(
  id: number | string | undefined
): Promise<void> {
  try {
    const response = await fetch(`${STRAPI_URL}/project/${String(id)}`, {
      credentials: "include",
      method: "DELETE",
    });

    if (!response.ok) {
      throw new Error(`delete project failed with status ${response.status}.`);
    }
    await invalidateAppQueries(["project"]);
    await invalidateAppQueries(["projects"]);
    await invalidateAppQueries(["overlay"]);
    await invalidateAppQueries(["overview"]);
    await invalidateAppQueries(["dashboard"]);
  } catch {
    return;
  }
}

export async function deleteAllProjects(): Promise<void> {
  try {
    const response = await fetch(`${STRAPI_URL}/project`, {
      credentials: "include",
      method: "DELETE",
    });

    if (!response.ok) {
      throw new Error(
        `delete all projects failed with status ${response.status}.`
      );
    }
    await invalidateAppQueries(["project"]);
    await invalidateAppQueries(["projects"]);
    await invalidateAppQueries(["overlay"]);
    await invalidateAppQueries(["overview"]);
    await invalidateAppQueries(["dashboard"]);
  } catch {
    return;
  }
}
